import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiArrowRight } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  const navItems = [
    { name: 'Home', id: 'home' },
    { name: 'Services', id: 'services' },
    { name: 'Solutions', id: 'solutions' },
    { name: 'About Us', id: 'about' },
    { name: 'Contact', id: 'contact' }
  ];

  const logoUrl = "https://img.gedhemedia.com/Logo_Primary_-_GedheMediaCom.png";

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-[120] transition-all duration-500 w-full ${
          isScrolled 
            ? 'bg-[#0A192F]/95 backdrop-blur-md border-b border-white/10 py-3 shadow-xl' 
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center">
              <a 
                href="#home" 
                onClick={(e) => scrollToSection(e, 'home')}
                className="relative z-[130]"
              >
                <img 
                  src={logoUrl} 
                  alt="GedheMedia" 
                  className={`h-8 md:h-12 w-auto max-w-[150px] md:max-w-none object-contain transition-all duration-300 ${isMenuOpen ? 'brightness-100' : 'brightness-0 invert'}`} 
                />
              </a>
            </div>

            <nav className="hidden md:flex items-center space-x-10">
              {navItems.map((item) => (
                <a 
                  key={item.name} 
                  href={`#${item.id}`} 
                  onClick={(e) => scrollToSection(e, item.id)}
                  className="text-sm tracking-[0.15em] uppercase font-black transition-all duration-300 hover:text-orange-500 text-white"
                >
                  {item.name}
                </a>
              ))}
              <a 
                href="https://wa.me/6283128418389" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="px-8 py-3 btn-primary text-white text-sm tracking-[0.1em] uppercase font-black rounded-full shadow-lg"
              >
                Consult Now
              </a>
            </nav>

            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden relative z-[130] p-2 focus:outline-none bg-white/10 rounded-full backdrop-blur-sm active:scale-90 transition-transform"
              aria-label="Toggle Menu"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-2xl text-white" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-0 bg-[#0A192F] z-[115] md:hidden flex flex-col w-full h-full"
          >
            <div className="pt-24 pb-6 flex-1 flex flex-col items-center justify-center px-8">
              <nav className="flex flex-col items-center w-full space-y-4">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                  >
                    <a 
                      href={`#${item.id}`}
                      className="text-[24px] font-black text-white hover:text-orange-500 transition-colors uppercase tracking-widest"
                      onClick={(e) => scrollToSection(e, item.id)}
                    >
                      {item.name}
                    </a>
                  </motion.div>
                ))}
              </nav>
              
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-12 w-full max-w-[280px]"
              >
                <a 
                  href="https://wa.me/6283128418389" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center justify-center w-full px-6 py-4 btn-primary text-white font-black rounded-2xl text-sm tracking-[0.2em] uppercase shadow-2xl"
                >
                  Start Strategy <SafeIcon icon={FiArrowRight} className="ml-2 text-lg" />
                </a>
              </motion.div>
            </div>

            <div className="py-8 w-full text-center border-t border-white/5 bg-black/20">
              <p className="text-gray-500 font-bold uppercase tracking-[0.4em] text-[10px]">
                GedheMedia &copy; {new Date().getFullYear()}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;